import type { ErrorInfo, ReactElement, ReactNode } from 'react';
import { Component, cloneElement } from 'react';

import { ScriptErrorDetail, ScriptErrorTrackService, getPosthogTracker } from '@shared/statistics';
import { logger } from '@luna/logger';

import { UnknownError } from './UnknownError';

const ERROR_NAME = 'ERROR';
const ERROR_TYPE = 'ERROR_JAVASCRIPT';

interface FallbackProps {
    error: Error | null;
    resetErrorBoundary: () => void;
}

type SharedErrorBoundaryProps = {
    onReset?: () => void;
    children: ReactNode;
    onError?: (error: ScriptErrorDetail) => void;
    stackMessageMaxLength?: number;
};

type ErrorBoundaryPropsWithErrorContent = {
    errorContent?: ReactNode;
    fallbackRender?: never;
} & SharedErrorBoundaryProps;

type ErrorBoundaryPropsWithFallbackRender = {
    errorContent?: never;
    fallbackRender?: (props: FallbackProps) => ReactNode;
} & SharedErrorBoundaryProps;

type Props = ErrorBoundaryPropsWithErrorContent | ErrorBoundaryPropsWithFallbackRender;

export class ErrorBoundary extends Component<Props> {
    tracker = new ScriptErrorTrackService(getPosthogTracker());

    state = { hasError: false, errorDetail: null as Error | null };

    static getDerivedStateFromError(error: Error | null) {
        return { hasError: true, errorDetail: error };
    }

    componentDidCatch(error: Error | null, __errorInfo: ErrorInfo) {
        const scriptErrorDetail = new ScriptErrorDetail(
            error?.name,
            error?.message,
            error?.stack,
            this.props.stackMessageMaxLength,
        );
        if (this.props.onError) {
            this.props.onError(scriptErrorDetail);
        } else {
            logger.error(error, __errorInfo);
        }
        this.tracker.track(ERROR_NAME, ERROR_TYPE, scriptErrorDetail);
    }

    resetErrorBoundary = () => {
        if (this.state.hasError) {
            this.props.onReset?.();
            this.setState({ hasError: false, errorDetail: null });
        }
    };

    render() {
        if (this.state.hasError && this.props.errorContent) {
            return cloneElement(this.props.errorContent as ReactElement<{ errorDetail: Error }>, {
                errorDetail: this.state.errorDetail!,
            });
        }

        if (this.state.hasError && typeof this.props.fallbackRender === 'function') {
            return this.props.fallbackRender({
                error: this.state.errorDetail,
                resetErrorBoundary: this.resetErrorBoundary,
            });
        }

        if (this.state.hasError) {
            return <UnknownError />;
        }

        return this.props.children;
    }
}
